import { Box, Typography } from '@mui/material';
import { ReactComponent as PlusIcon } from 'assets/add-white.svg';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { IOSSwitch } from 'components/shared/IOSSwitch';
import { LayoutSwitcher } from 'components/shared/LayoutSwitcher';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { createInitialFilterState } from '../../utils';
import { FilterBadge } from './components/FilterBadge';
import { FilterPopover } from './components/FilterPopover';
import { calculateActiveFilterCount } from './DealsFilters.utils';
import styles from './styles.module.css';
import { DealsFiltersProps } from './types';
import { usePermissions } from 'customHook/usePermissions';
import zeplinColor from 'theme/Color';

export const DealsFilters = ({
  layout,
  onLayoutChange,
  filterState,
  onFilterChange,
  tabValue,
  onTabChange,
  onSearchChange,
  needsAttention = false,
  onNeedsAttentionChange,
  handleSetCurrentPage,
  showTabs = true,
  isOwnerFilterVisible,
  hideClientFilter = false,
  lockedClient = null,
  newDealState,
}: DealsFiltersProps) => {
  const [filterPopoverAnchor, setFilterPopoverAnchor] = useState<HTMLButtonElement | null>(null);

  const tabs = [
    {
      label: 'My Deals',
      id: 1,
      isHidden: tabValue !== 0,
      Component: <></>,
    },
    {
      label: 'All Deals',
      id: 2,
      isHidden: tabValue !== 1,
      Component: <></>,
    },
  ];

  const handleSearchDeals = (term: string) => {
    onSearchChange?.(term);
    handleSetCurrentPage(0);
  };

  const { hasAction } = usePermissions();
  const canCreateDeal = hasAction('Deals', 'AllDeals', 'create');

  const handleNeedsAttentionSwitch = () => {
    onNeedsAttentionChange?.(!needsAttention);
  };

  const clearAllFilters = () => {
    const cleared = createInitialFilterState();
    if (lockedClient) {
      cleared.client = [lockedClient];
    }
    onFilterChange(cleared);
  };

  const handleFilterButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterPopoverAnchor(event.currentTarget);
  };

  const handleFilterPopoverClose = () => {
    setFilterPopoverAnchor(null);
  };

  const activeFilterCount = calculateActiveFilterCount(filterState);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>{showTabs && <CustomTab tabs={tabs} handleChange={onTabChange} value={tabValue} />}</Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          gap: { xs: '8px', sm: '12px' },
          alignItems: 'center',
          marginBottom: '4px',
        }}
      >
        <Box sx={{ width: { xl: '240px', xs: '150px' } }}>
          <CustomSearch getProjects={handleSearchDeals} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
              whiteSpace: 'nowrap',
            }}
          >
            Needs Attention
          </Typography>
          <IOSSwitch checked={needsAttention} onChange={handleNeedsAttentionSwitch} />
        </Box>

        <FilterBadge activeFilterCount={activeFilterCount} onClick={handleFilterButtonClick} />

        <LayoutSwitcher layout={layout} onLayoutChange={onLayoutChange} />

        {canCreateDeal ? (
          <Link to={RoutePaths.Deals + '/new'} state={newDealState} className={styles.link}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: zeplinColor.Brand1,
              }}
            >
              <PlusIcon color={zeplinColor.Neutral1} />
              <Typography variant='responsiveButtonMedium'> New Deal</Typography>
            </Box>
          </Link>
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: 0.4,
              cursor: 'not-allowed',
              backgroundColor: zeplinColor.Neutral6,
              padding: '8px 12px',
              borderRadius: '8px',
            }}
          >
            <PlusIcon color={zeplinColor.Neutral4} />
            <Typography variant='responsiveButtonMedium'> New Deal</Typography>
          </Box>
        )}

        <FilterPopover
          anchorEl={filterPopoverAnchor}
          onClose={handleFilterPopoverClose}
          filterState={filterState}
          onFilterChange={onFilterChange}
          onClearAll={clearAllFilters}
          isOwnerFilterVisible={isOwnerFilterVisible ?? Boolean(tabValue)}
          hideClientFilter={hideClientFilter}
          lockedClient={lockedClient}
        />
      </Box>
    </Box>
  );
};
